"use client";

import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button, buttonVariants } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { cn } from "@workspace/ui/lib/utils";

import { printerCommandFailureMessage } from "../printer-command-failure";
import { printersApi } from "../printers-api";
import { registeredPrinterOf, type PrinterRecord } from "../printers-types";
import { ipSchema } from "../validation";
import { ConfirmTestPrintDialog } from "./confirm-test-print-dialog";

const schema = z.object({
	ip: ipSchema,
	label: z.string().max(100),
});

type FormValues = { ip: string; label: string };

export function RegisterNetworkPrinterDialog({
	open,
	onOpenChange,
	tenant,
	hostname,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	hostname: string;
}) {
	const queryClient = useQueryClient();
	const [notApproved, setNotApproved] = React.useState(false);
	const [registered, setRegistered] = React.useState<PrinterRecord | null>(
		null,
	);

	const register = useMutation(
		printersApi.registerNetworkPrinterOptions(tenant, hostname),
	);

	const form = useAppForm({
		defaultValues: { ip: "", label: "" } as FormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: schema },
		onSubmit: async ({ value }) => {
			setNotApproved(false);
			const label = value.label.trim();
			const result = await register.mutateAsync({
				ip: value.ip.trim(),
				// Blank: the server names it ("Network printer").
				...(label ? { label } : {}),
			});

			if (result.success) {
				toast.success("Network printer registered.");
				await queryClient.invalidateQueries({
					queryKey: [tenant, "print-proxies", hostname, "printers"],
				});
				// HA-945: a test page goes to the new printer and the user says whether it printed.
				setRegistered(registeredPrinterOf(result.response));
				handleClose(false);
			} else if (result.code === "NOT_APPROVED") {
				setNotApproved(true);
			} else {
				toast.error(
					printerCommandFailureMessage(
						result.code,
						"Failed to register printer.",
					),
				);
			}
		},
	});

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			register.reset();
			setNotApproved(false);
		}
		onOpenChange(o);
	};

	return (
		<>
			<Dialog open={open} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Add network printer</DialogTitle>
						<DialogDescription>
							Register a printer by network address.
						</DialogDescription>
					</DialogHeader>

					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						{notApproved && (
							<Alert variant="destructive">
								<AlertCircle className="size-4" />
								<AlertDescription className="flex items-center justify-between gap-2">
									This printer isn&apos;t supported.
									<a
										href="mailto:support@bookgusto.com?subject=Printer support request"
										className={cn(
											buttonVariants({ variant: "outline", size: "sm" }),
										)}
									>
										Request support
									</a>
								</AlertDescription>
							</Alert>
						)}

						<form.AppField name="ip">
							{(field) => (
								<field.FormItem>
									<field.FormLabel>IP address</field.FormLabel>
									<field.FormControl>
										<Input
											name={field.name}
											placeholder="e.g. 192.168.1.50"
											inputMode="decimal"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={field.handleBlur}
										/>
									</field.FormControl>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>

						<form.AppField name="label">
							{(field) => (
								<field.FormItem>
									<field.FormLabel>Label (optional)</field.FormLabel>
									<field.FormControl>
										<Input
											name={field.name}
											placeholder="Network printer"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={field.handleBlur}
										/>
									</field.FormControl>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>

						<DialogFooter>
							<form.Subscribe selector={(state) => [state.canSubmit]}>
								{([canSubmit]) => (
									<>
										<Button
											type="button"
											variant="outline"
											onClick={() => handleClose(false)}
											disabled={register.isPending}
										>
											Cancel
										</Button>
										<Button
											type="submit"
											disabled={!canSubmit || register.isPending}
										>
											{register.isPending && (
												<Loader2 className="mr-2 size-4 animate-spin" />
											)}
											Register
										</Button>
									</>
								)}
							</form.Subscribe>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
			{registered && (
				<ConfirmTestPrintDialog
					open
					onOpenChange={(o) => !o && setRegistered(null)}
					tenant={tenant}
					hostname={hostname}
					printer={registered}
				/>
			)}
		</>
	);
}
